import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { ApiService, BASE_CONFIG } from '@libs/core/base-config';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { asResult, AsyncState, initialState } from '@libs/core/lib/state/async-states';
import { UploadService } from '@libs/mtjr/shared/attachment/upload_service';
import {
  withGetSubOrderChat,
  withMarkChatMessageAsSeen,
  withSendChatMessage,
  withUpdateChatMessage,
} from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { getSubOrderChat } from '@libs/mtjr/shared/generated/entities/order/service/order.service';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const OrderChatStore = signalStore(
  withSendChatMessage(),
  withUpdateChatMessage(),
  withMarkChatMessageAsSeen(),
  withGetSubOrderChat(),
  withMethods((store, apiService = inject(ApiService)) => ({
    async refreshSubOrderChat(input: { orderId: string; subOrderId: string }) {
      const result = await asResult(getSubOrderChat(apiService, input));
      patchState(store, { getSubOrderChatState: result });
    },
    addLocalChatMessage(chat: Entity.ChatMessage) {
      const state = store.getSubOrderChatState();
      if (!state.success) return;
      const updatedChat = [...state.data.chat, chat];
      patchState(store, {
        getSubOrderChatState: {
          ...state,
          data: { ...state.data, chat: updatedChat },
        },
      });
    },
  })),
);

type ChatWithPosition = Entity.ChatMessage & { position: 'start' | 'end' };

@Component({
  selector: 'mtjr-orders-chat',
  providers: [OrderChatStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonContent,
    IonText,
    IonButton,
    IonIcon,
    NgClass,
    IonTextarea,
    IonModal,
    IonSpinner,
    DatePipe,
    VxImageComponent,
    TranslatePipe,
  ],
  templateUrl: './order-chat.page.html',
})
export class OrderChatPage {
  order = input.required<Entity.Order>();
  subOrder = input.required<Entity.SubOrder>();

  reversedChats = computed<ChatWithPosition[]>(() => {
    const pendingChat =
      this.isSending() && this.chatText().length > 0
        ? ({
            id: 'pending',
            subOrderId: this.subOrder().id,
            text: this.chatText(),
            attachment: this.attachmentUrl(),
            userType: this.flavor === 'vendors' ? 'vendor' : this.flavor === 'admins' ? 'admin' : 'customer',
            createdAt: new Date(),
            reads: [],
            userId: '',
          } satisfies Entity.ChatMessage)
        : undefined;

    const state = this.store.getSubOrderChatState();
    if (!state.success) return [];

    const messages = [...state.data.chat].reverse();
    if (pendingChat) {
      messages.unshift(pendingChat);
    }
    if (!messages) return [];
    return messages.map((chat) => {
      if (this.flavor === 'vendors') {
        return {
          ...chat,
          position: chat.userType === 'vendor' ? 'start' : 'end',
        };
      } else if (this.flavor === 'admins') {
        return {
          ...chat,
          position: chat.userType === 'admin' ? 'start' : 'end',
        };
      } else {
        return {
          ...chat,
          position: chat.userType === 'customer' ? 'start' : 'end',
        };
      }
    });
  });
  store = inject(OrderChatStore);
  chatText = signal('');
  attachment = signal<File | undefined>(undefined);
  attachmentUrl = signal<string | undefined>(undefined);
  uploadService = inject(UploadService);
  imageView = signal<string | undefined>(undefined);
  attachmentSendState = signal<AsyncState<Entity.Attachment>>(initialState());
  userStore = inject(UserStore);
  eventBus = inject(NgEventBus);
  eventsSub?: Subscription;
  flavor = inject(BASE_CONFIG).flavor;
  pendingAttachment = signal<string | undefined>(undefined);

  isSending = computed(
    () => this.store.sendChatMessageState().type === 'loading' || this.attachmentSendState().type === 'loading',
  );

  onTextChanged(event: Event) {
    const input = event.target as HTMLInputElement;
    // the file input calls this method as well for some reason
    // maybe the focus manager is confused because of the hidden file input
    // this is a workaround to avoid that
    if (input.type === 'textarea') {
      this.chatText.set(input.value);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  onFilesPicked($event: Event) {
    const input = $event.target as HTMLInputElement;
    const files = input.files;
    if (files) {
      this.attachment.set(files[0]);
    }
  }

  openAttachment(file: File) {
    this.imageView.set(URL.createObjectURL(file));
  }

  reload() {
    this.store.refreshSubOrderChat({ orderId: this.order().id, subOrderId: this.subOrder().id });
  }

  send() {
    const attachment = this.attachment();
    if (this.chatText() === '' && !attachment) return;
    if (attachment) {
      this.uploadService.upload(attachment, { key: 'chat-' + this.order().id }).subscribe((state) => {
        this.attachmentSendState.set(state);
        if (state.success) {
          this.pendingAttachment.set(state.data.url);
          this.store.sendChatMessage(
            {
              text: this.chatText(),
              attachment: state.data.url,
            },
            { orderId: this.order().id, subOrderId: this.subOrder().id },
          );
        }
      });
    } else {
      this.store.sendChatMessage(
        { text: this.chatText() },
        { orderId: this.order().id, subOrderId: this.subOrder().id },
      );
    }
  }

  constructor() {
    effect(() => {
      const subOrder = this.subOrder();
      const order = this.order();
      if (subOrder && order) {
        untracked(() => {
          this.store.getSubOrderChat({ orderId: order.id, subOrderId: subOrder.id });
        });
      }
    });

    this.eventsSub = this.eventBus
      .on<{ orderId: string; subOrderId: string }>('order:fetch-chat')
      .subscribe((event) => {
        const orderId = event.data?.orderId;
        const subOrderId = event.data?.subOrderId;
        if (orderId && subOrderId && orderId === this.order().id && subOrderId === this.subOrder().id) {
          this.reload();
        }
      });

    effect(() => {
      const state = this.store.sendChatMessageState();
      if (state.success) {
        untracked(() => {
          if (this.chatText().length > 0) {
            this.store.addLocalChatMessage({
              id: 'local' + Date.now(),
              subOrderId: this.subOrder().id,
              text: this.chatText(),
              attachment: this.pendingAttachment(),
              userType: this.flavor === 'vendors' ? 'vendor' : this.flavor === 'admins' ? 'admin' : 'customer',
              createdAt: new Date(),
              reads: [],
              userId: this.userStore.userId() || '',
            });
          }
          this.pendingAttachment.set(undefined);
          this.chatText.set('');
          this.attachment.set(undefined);
          this.reload();
        });
      }
    });
    effect(() => {
      const userState = this.userStore.loggedInUserState();
      if (!userState.success) return;
      const userId = userState.data.id;
      const chats = this.reversedChats();
      if (chats && chats.length > 0) {
        const lastChat = chats[0];
        const hasRead = lastChat.reads?.some((read) => read.userId === userId);
        if (!hasRead && lastChat.id !== 'pending') {
          untracked(() => {
            console.log({ lastChat });

            this.store.markChatMessageAsSeen({
              orderId: this.order().id,
              subOrderId: this.subOrder().id,
              messageId: lastChat.id,
            });
          });
        }
      }
    });
  }
}
